<section
    *ngIf="state as current"
    class="flex-monitor-status"
    [attr.aria-busy]="current.isLoading"
    aria-labelledby="flex-monitor-status-heading"
>
    <header class="status-toolbar non-printable">
        <div class="status-heading">
            <h1 id="flex-monitor-status-heading">{{ 'DIAGNOSTICS.MONITOR_STATUS' | translate }}</h1>
        </div>
        <div class="action-buttons">
            <button
                mat-button
                type="button"
                [matTooltip]="'DIAGNOSTICS.FLEX_MONITOR_STATUS.PRINT_HELP' | translate"
                (click)="print()"
            >
                <mat-icon>print</mat-icon>
                {{ 'DIAGNOSTICS.FLEX_MONITOR_STATUS.PRINT' | translate }}
            </button>
            <button mat-button type="button" (click)="close()">
                <mat-icon>close</mat-icon>
                {{ 'COMMON.CLOSE' | translate }}
            </button>
        </div>
    </header>

    <header class="print-header printable-only">
        <h1>{{ 'DIAGNOSTICS.FLEX_DIAGNOSTIC_SUPPORT' | translate }}</h1>
        <h2>{{ 'DIAGNOSTICS.MONITOR_STATUS' | translate }}</h2>
        <div>{{ location?.locationName || dialogData.locationName || unavailableText }}</div>
        <div class="identifier">{{ location?.serialNumber || dialogData.deviceId || unavailableText }}</div>
    </header>

    <div class="visually-hidden" aria-live="polite">
        <span *ngIf="current.isLoading && !current.isRefreshing">{{
            'DIAGNOSTICS.FLEX_MONITOR_STATUS.LOADING' | translate
        }}</span>
        <span *ngIf="current.isRefreshing">{{ 'DIAGNOSTICS.FLEX_MONITOR_STATUS.REFRESHING' | translate }}</span>
        <span *ngIf="current.queueState === 'queueing'">{{
            'DIAGNOSTICS.FLEX_MONITOR_STATUS.COMMAND_QUEUEING' | translate
        }}</span>
        <span *ngIf="current.queueState === 'queued'">{{
            'DIAGNOSTICS.FLEX_MONITOR_STATUS.COMMAND_QUEUED' | translate
        }}</span>
        <span *ngIf="current.queueState === 'error'">{{
            'DIAGNOSTICS.FLEX_MONITOR_STATUS.COMMAND_ERROR' | translate
        }}</span>
        <span *ngIf="copyMessage">{{ copyMessage }}</span>
    </div>

    <app-loader
        [isLoading]="current.status.state === 'loading' && !current.status.data"
        [isPositionAbsolute]="false"
    ></app-loader>

    <section class="status-section overview-section" aria-labelledby="flex-overview-heading">
        <h2 id="flex-overview-heading" class="visually-hidden">
            {{ 'DIAGNOSTICS.FLEX_MONITOR_STATUS.OVERVIEW' | translate }}
        </h2>
        <div class="overview-grid">
            <div class="identity-summary">
                <div class="field-label">{{ 'DIAGNOSTICS.FLEX_MONITOR_STATUS.LOCATION_NAME' | translate }}</div>
                <div class="location-name">
                    {{ location?.locationName || dialogData.locationName || unavailableText }}
                </div>
            </div>
            <app-flex-status-gauge
                [label]="'DIAGNOSTICS.FLEX_MONITOR_STATUS.SIGNAL_STRENGTH' | translate"
                [value]="status?.cellular?.signalStrengthDbm?.value"
                unit="dBm"
                [state]="signalState"
                [configuration]="signalGaugeConfiguration"
                [precision]="0"
                [unavailableText]="unavailableText"
                [stateText]="gaugeStateText(signalState)"
                [isDarkTheme]="isDarkTheme && !isPrinting"
            ></app-flex-status-gauge>
            <app-flex-status-gauge
                [label]="'DIAGNOSTICS.FLEX_MONITOR_STATUS.BATTERY_VOLTAGE' | translate"
                [value]="status?.device?.batteryVoltage?.value"
                unit="V"
                [state]="batteryState"
                [configuration]="batteryGaugeConfiguration"
                [precision]="1"
                [unavailableText]="unavailableText"
                [stateText]="gaugeStateText(batteryState)"
                [isDarkTheme]="isDarkTheme && !isPrinting"
            ></app-flex-status-gauge>
        </div>
        <div class="resource-error" *ngIf="hasResourceError('status')">
            {{ 'DIAGNOSTICS.FLEX_MONITOR_STATUS.STATUS_ERROR' | translate }}
        </div>
    </section>

    <section class="status-section" aria-labelledby="flex-device-heading">
        <h2 id="flex-device-heading">{{ 'DIAGNOSTICS.FLEX_MONITOR_STATUS.DEVICE_INFORMATION' | translate }}</h2>
        <div class="detail-grid device-grid">
            <div class="detail-cell">
                <span>{{ 'DIAGNOSTICS.FLEX_MONITOR_STATUS.IOT_ID' | translate }}</span>
                <div class="copyable-value">
                    <strong class="identifier">{{
                        location?.serialNumber || dialogData.deviceId || unavailableText
                    }}</strong>
                    <button
                        *ngIf="canCopy(location?.serialNumber || dialogData.deviceId)"
                        mat-icon-button
                        type="button"
                        class="copy-button non-printable"
                        [class.copied]="isCopied('serialNumber', location?.serialNumber || dialogData.deviceId)"
                        [attr.aria-label]="
                            copyActionText(
                                'serialNumber',
                                location?.serialNumber || dialogData.deviceId,
                                'DIAGNOSTICS.FLEX_MONITOR_STATUS.IOT_ID'
                            )
                        "
                        [matTooltip]="
                            copyActionText(
                                'serialNumber',
                                location?.serialNumber || dialogData.deviceId,
                                'DIAGNOSTICS.FLEX_MONITOR_STATUS.IOT_ID'
                            )
                        "
                        (click)="
                            copyValue(
                                location?.serialNumber || dialogData.deviceId,
                                'serialNumber',
                                'DIAGNOSTICS.FLEX_MONITOR_STATUS.IOT_ID'
                            )
                        "
                    >
                        <mat-icon>{{
                            isCopied('serialNumber', location?.serialNumber || dialogData.deviceId)
                                ? 'check'
                                : 'content_copy'
                        }}</mat-icon>
                    </button>
                </div>
            </div>
            <div class="detail-cell">
                <span>{{ 'DIAGNOSTICS.FLEX_MONITOR_STATUS.PHYSICAL_MONITOR_SERIAL' | translate }}</span>
                <div class="copyable-value">
                    <strong class="identifier">{{
                        status?.device?.physicalMonitorSerialNumber?.value || unavailableText
                    }}</strong>
                    <button
                        *ngIf="canCopy(status?.device?.physicalMonitorSerialNumber?.value)"
                        mat-icon-button
                        type="button"
                        class="copy-button non-printable"
                        [class.copied]="
                            isCopied('physicalMonitorSerial', status?.device?.physicalMonitorSerialNumber?.value)
                        "
                        [attr.aria-label]="
                            copyActionText(
                                'physicalMonitorSerial',
                                status?.device?.physicalMonitorSerialNumber?.value,
                                'DIAGNOSTICS.FLEX_MONITOR_STATUS.PHYSICAL_MONITOR_SERIAL'
                            )
                        "
                        [matTooltip]="
                            copyActionText(
                                'physicalMonitorSerial',
                                status?.device?.physicalMonitorSerialNumber?.value,
                                'DIAGNOSTICS.FLEX_MONITOR_STATUS.PHYSICAL_MONITOR_SERIAL'
                            )
                        "
                        (click)="
                            copyValue(
                                status?.device?.physicalMonitorSerialNumber?.value,
                                'physicalMonitorSerial',
                                'DIAGNOSTICS.FLEX_MONITOR_STATUS.PHYSICAL_MONITOR_SERIAL'
                            )
                        "
                    >
                        <mat-icon>{{
                            isCopied('physicalMonitorSerial', status?.device?.physicalMonitorSerialNumber?.value)
                                ? 'check'
                                : 'content_copy'
                        }}</mat-icon>
                    </button>
                </div>
            </div>
            <div class="detail-cell">
                <span>{{ 'DIAGNOSTICS.FLEX_MONITOR_STATUS.LAST_ACTIVITY' | translate }}</span>
                <strong>{{ formatStatusTimestamp(status?.timestamps?.lastActivityAtUtc) }}</strong>
            </div>
            <div class="detail-cell">
                <span>{{ 'DIAGNOSTICS.FIRMWARE_VERSION' | translate }}</span>
                <strong>{{ status?.device?.firmwareVersion?.value || unavailableText }}</strong>
            </div>
            <div class="detail-cell">
                <span>{{ 'DIAGNOSTICS.FLEX_MONITOR_STATUS.IMEI' | translate }}</span>
                <div class="copyable-value">
                    <strong class="identifier">{{ status?.cellular?.imei?.value || unavailableText }}</strong>
                    <button
                        *ngIf="canCopy(status?.cellular?.imei?.value)"
                        mat-icon-button
                        type="button"
                        class="copy-button non-printable"
                        [class.copied]="isCopied('imei', status?.cellular?.imei?.value)"
                        [attr.aria-label]="
                            copyActionText(
                                'imei',
                                status?.cellular?.imei?.value,
                                'DIAGNOSTICS.FLEX_MONITOR_STATUS.IMEI'
                            )
                        "
                        [matTooltip]="
                            copyActionText(
                                'imei',
                                status?.cellular?.imei?.value,
                                'DIAGNOSTICS.FLEX_MONITOR_STATUS.IMEI'
                            )
                        "
                        (click)="
                            copyValue(status?.cellular?.imei?.value, 'imei', 'DIAGNOSTICS.FLEX_MONITOR_STATUS.IMEI')
                        "
                    >
                        <mat-icon>{{
                            isCopied('imei', status?.cellular?.imei?.value) ? 'check' : 'content_copy'
                        }}</mat-icon>
                    </button>
                </div>
            </div>
            <div class="detail-cell">
                <span>{{ 'DIAGNOSTICS.FLEX_MONITOR_STATUS.ICCID' | translate }}</span>
                <div class="copyable-value">
                    <strong class="identifier">{{ status?.cellular?.iccid?.value || unavailableText }}</strong>
                    <button
                        *ngIf="canCopy(status?.cellular?.iccid?.value)"
                        mat-icon-button
                        type="button"
                        class="copy-button non-printable"
                        [class.copied]="isCopied('iccid', status?.cellular?.iccid?.value)"
                        [attr.aria-label]="
                            copyActionText(
                                'iccid',
                                status?.cellular?.iccid?.value,
                                'DIAGNOSTICS.FLEX_MONITOR_STATUS.ICCID'
                            )
                        "
                        [matTooltip]="
                            copyActionText(
                                'iccid',
                                status?.cellular?.iccid?.value,
                                'DIAGNOSTICS.FLEX_MONITOR_STATUS.ICCID'
                            )
                        "
                        (click)="
                            copyValue(status?.cellular?.iccid?.value, 'iccid', 'DIAGNOSTICS.FLEX_MONITOR_STATUS.ICCID')
                        "
                    >
                        <mat-icon>{{
                            isCopied('iccid', status?.cellular?.iccid?.value) ? 'check' : 'content_copy'
                        }}</mat-icon>
                    </button>
                </div>
            </div>
            <div class="detail-cell">
                <span>{{ 'DIAGNOSTICS.FLEX_MONITOR_STATUS.MODEM_VERSION' | translate }}</span>
                <strong>{{ status?.cellular?.modemVersion?.value || unavailableText }}</strong>
            </div>
            <div class="detail-cell">
                <span>{{ 'DIAGNOSTICS.FLEX_MONITOR_STATUS.SIGNAL_STRENGTH' | translate }}</span>
                <strong>
                    {{
                        status?.cellular?.signalStrengthDbm?.value !== null &&
                        status?.cellular?.signalStrengthDbm?.value !== undefined
                            ? (status.cellular.signalStrengthDbm.value | number : '1.0-0') + ' dBm'
                            : unavailableText
                    }}
                    <span class="device-status" [ngClass]="signalState">- {{ gaugeStateText(signalState) }}</span>
                </strong>
            </div>
            <div class="detail-cell">
                <span>{{ 'DIAGNOSTICS.FLEX_MONITOR_STATUS.BATTERY_VOLTAGE' | translate }}</span>
                <strong>
                    {{
                        status?.device?.batteryVoltage?.value !== null &&
                        status?.device?.batteryVoltage?.value !== undefined
                            ? (status.device.batteryVoltage.value | number : '1.1-1') + ' V'
                            : unavailableText
                    }}
                    <span class="device-status" [ngClass]="batteryState">- {{ gaugeStateText(batteryState) }}</span>
                </strong>
            </div>
        </div>
    </section>

    <section class="status-section installation-section" aria-labelledby="flex-installation-heading">
        <app-loader
            [isLoading]="current.location.state === 'loading' || current.configuration.state === 'loading'"
            [isPositionAbsolute]="false"
        ></app-loader>
        <h2 id="flex-installation-heading">
            {{ 'DIAGNOSTICS.FLEX_MONITOR_STATUS.INSTALLATION_LOCATION' | translate }}
        </h2>
        <div class="installation-layout">
            <div class="installation-information">
                <app-flex-installation-drawing
                    [resource]="current.installationDrawing"
                    [installationType]="location?.installationType || ''"
                    [caption]="installationCaption"
                    [ariaLabel]="installationDrawingAriaLabel"
                    [isDarkTheme]="isDarkTheme && !isPrinting"
                ></app-flex-installation-drawing>
                <div class="detail-grid installation-detail-grid">
                    <div class="detail-cell">
                        <span>{{ 'DIAGNOSTICS.FLEX_MONITOR_STATUS.INSTALLATION_TYPE' | translate }}</span>
                        <strong>{{ location?.installationType || unavailableText }}</strong>
                    </div>
                    <div class="detail-cell">
                        <span>{{ 'DIAGNOSTICS.FLEX_MONITOR_STATUS.INSTALLATION_SHAPE' | translate }}</span>
                        <strong>{{ location?.installationShape || unavailableText }}</strong>
                    </div>
                    <div class="detail-cell">
                        <span>{{ 'DIAGNOSTICS.FLEX_MONITOR_STATUS.HEIGHT' | translate }}</span>
                        <strong>{{
                            locationHeight !== null
                                ? locationHeight + ' ' + (location?.depthUnit || '')
                                : unavailableText
                        }}</strong>
                    </div>
                    <div class="detail-cell">
                        <span>{{ 'DIAGNOSTICS.FLEX_MONITOR_STATUS.WIDTH' | translate }}</span>
                        <strong>{{
                            locationWidth !== null ? locationWidth + ' ' + (location?.depthUnit || '') : unavailableText
                        }}</strong>
                    </div>
                    <div class="detail-cell" *ngIf="location?.range !== null && location?.range !== undefined">
                        <span>{{ 'DIAGNOSTICS.FLEX_MONITOR_STATUS.RANGE' | translate }}</span>
                        <strong>{{ location.range }} {{ location.depthUnit }}</strong>
                    </div>
                    <div class="detail-cell" *ngIf="location?.capacity !== null && location?.capacity !== undefined">
                        <span>{{ 'DIAGNOSTICS.FLEX_MONITOR_STATUS.CAPACITY' | translate }}</span>
                        <strong>{{ location.capacity }} {{ location.flowUnit }}</strong>
                    </div>
                    <div class="detail-cell" *ngIf="location?.length !== null && location?.length !== undefined">
                        <span>{{ 'DIAGNOSTICS.FLEX_MONITOR_STATUS.LENGTH' | translate }}</span>
                        <strong>{{ location.length }} {{ location.depthUnit }}</strong>
                    </div>
                    <div class="detail-cell" *ngIf="location?.breadth !== null && location?.breadth !== undefined">
                        <span>{{ 'DIAGNOSTICS.FLEX_MONITOR_STATUS.BREADTH' | translate }}</span>
                        <strong>{{ location.breadth }} {{ location.depthUnit }}</strong>
                    </div>
                    <div
                        class="detail-cell"
                        *ngIf="location?.coefficient !== null && location?.coefficient !== undefined"
                    >
                        <span>{{ 'DIAGNOSTICS.FLEX_MONITOR_STATUS.COEFFICIENT' | translate }}</span>
                        <strong>{{ location.coefficient }}</strong>
                    </div>
                    <div class="detail-cell">
                        <span>{{ 'DIAGNOSTICS.FLEX_MONITOR_STATUS.LATITUDE' | translate }}</span>
                        <strong>{{ formatCoordinate(latitude) }}</strong>
                    </div>
                    <div class="detail-cell">
                        <span>{{ 'DIAGNOSTICS.FLEX_MONITOR_STATUS.LONGITUDE' | translate }}</span>
                        <strong>{{ formatCoordinate(longitude) }}</strong>
                    </div>
                    <div class="detail-cell field-wide">
                        <span>{{ 'DIAGNOSTICS.FLEX_MONITOR_STATUS.ADDRESS' | translate }}</span>
                        <strong>{{ location?.manholeAddress || unavailableText }}</strong>
                    </div>
                </div>
            </div>
            <app-flex-static-map
                [latitude]="latitude"
                [longitude]="longitude"
                [locationName]="location?.locationName || dialogData.locationName"
                [unavailableText]="unavailableText"
                [isDarkTheme]="isDarkTheme && !isPrinting"
                [retryToken]="status?.timestamps?.retrievedAtUtc || ''"
                [locationStatus]="location?.status ?? null"
                [ariaLabel]="
                    ('DIAGNOSTICS.FLEX_MONITOR_STATUS.MAP_ARIA' | translate) +
                    ' ' +
                    (location?.locationName || dialogData.locationName)
                "
            ></app-flex-static-map>
        </div>

        <div class="configuration-layout">
            <div class="configuration-group">
                <h3>{{ 'DIAGNOSTICS.FLEX_MONITOR_STATUS.DELIVERY_SAMPLING' | translate }}</h3>
                <div class="detail-grid configuration-grid">
                    <div class="detail-cell">
                        <span>{{ 'DIAGNOSTICS.FLEX_MONITOR_STATUS.CURRENT_DELIVERY' | translate }}</span>
                        <strong>{{
                            status?.dataDelivery?.commonAcrossProfilesAppliedSeconds !== null &&
                            status?.dataDelivery?.commonAcrossProfilesAppliedSeconds !== undefined
                                ? (status.dataDelivery.commonAcrossProfilesAppliedSeconds | secondsToSampleRatePipe)
                                : unavailableText
                        }}</strong>
                    </div>
                    <div class="detail-cell">
                        <span>{{ 'DIAGNOSTICS.FLEX_MONITOR_STATUS.CONFIGURED_NORMAL_SAMPLE' | translate }}</span>
                        <strong>{{
                            current.configuration.data?.normalStorageInterval !== null &&
                            current.configuration.data?.normalStorageInterval !== undefined
                                ? (current.configuration.data.normalStorageInterval | secondsToSampleRatePipe)
                                : unavailableText
                        }}</strong>
                    </div>
                    <div class="detail-cell">
                        <span>{{ 'DIAGNOSTICS.FLEX_MONITOR_STATUS.CONFIGURED_FAST_SAMPLE' | translate }}</span>
                        <strong>{{
                            current.configuration.data?.fastStorageInterval !== null &&
                            current.configuration.data?.fastStorageInterval !== undefined
                                ? (current.configuration.data.fastStorageInterval | secondsToSampleRatePipe)
                                : unavailableText
                        }}</strong>
                    </div>
                    <div class="detail-cell">
                        <span>{{ 'DIAGNOSTICS.FLEX_MONITOR_STATUS.CONFIGURED_NORMAL_DELIVERY' | translate }}</span>
                        <strong>{{
                            current.configuration.data?.telemetryNormalInterval !== null &&
                            current.configuration.data?.telemetryNormalInterval !== undefined
                                ? (current.configuration.data.telemetryNormalInterval | secondsToSampleRatePipe)
                                : unavailableText
                        }}</strong>
                    </div>
                    <div class="detail-cell">
                        <span>{{ 'DIAGNOSTICS.FLEX_MONITOR_STATUS.CONFIGURED_FAST_DELIVERY' | translate }}</span>
                        <strong>{{
                            current.configuration.data?.telemetryFastInterval !== null &&
                            current.configuration.data?.telemetryFastInterval !== undefined
                                ? (current.configuration.data.telemetryFastInterval | secondsToSampleRatePipe)
                                : unavailableText
                        }}</strong>
                    </div>
                </div>
            </div>

            <div class="configuration-group">
                <h3>{{ 'DIAGNOSTICS.FLEX_MONITOR_STATUS.MP1_ALARMS' | translate }}</h3>
                <div class="alarm-grid" *ngIf="mp1Alarm; else noAlarms">
                    <div class="detail-cell">
                        <span>{{ 'DIAGNOSTICS.FLEX_MONITOR_STATUS.LOW_LEVEL' | translate }}</span>
                        <strong
                            >{{
                                mp1Alarm.lowLevelEnable
                                    ? ('COMMON.YES_TEXT' | translate)
                                    : ('COMMON.NO_TEXT' | translate)
                            }}
                            · {{ mp1Alarm.lowLevelThreshold }} {{ location?.depthUnit }}</strong
                        >
                    </div>
                    <div class="detail-cell">
                        <span>{{ 'DIAGNOSTICS.FLEX_MONITOR_STATUS.FULL_PIPE' | translate }}</span>
                        <strong
                            >{{
                                mp1Alarm.fullPipeEnable
                                    ? ('COMMON.YES_TEXT' | translate)
                                    : ('COMMON.NO_TEXT' | translate)
                            }}
                            · {{ mp1Alarm.fullPipeThreshold }} {{ location?.depthUnit }}</strong
                        >
                    </div>
                    <div class="detail-cell">
                        <span>{{ 'DIAGNOSTICS.FLEX_MONITOR_STATUS.HIGH_LEVEL' | translate }}</span>
                        <strong
                            >{{
                                mp1Alarm.highLevelEnable
                                    ? ('COMMON.YES_TEXT' | translate)
                                    : ('COMMON.NO_TEXT' | translate)
                            }}
                            · {{ mp1Alarm.highLevelThreshold }} {{ location?.depthUnit }}</strong
                        >
                    </div>
                    <div class="detail-cell">
                        <span>{{ 'DIAGNOSTICS.FLEX_MONITOR_STATUS.HIGH_HIGH' | translate }}</span>
                        <strong
                            >{{
                                mp1Alarm.highHighEnable
                                    ? ('COMMON.YES_TEXT' | translate)
                                    : ('COMMON.NO_TEXT' | translate)
                            }}
                            · {{ mp1Alarm.highHighThreshold }} {{ location?.depthUnit }}</strong
                        >
                    </div>
                    <div class="detail-cell">
                        <span>{{ 'DIAGNOSTICS.FLEX_MONITOR_STATUS.HIGH_FLOW' | translate }}</span>
                        <strong
                            >{{
                                mp1Alarm.highFlowEnable
                                    ? ('COMMON.YES_TEXT' | translate)
                                    : ('COMMON.NO_TEXT' | translate)
                            }}
                            · {{ mp1Alarm.highFlowThreshold }} {{ location?.flowUnit }}</strong
                        >
                    </div>
                </div>
                <ng-template #noAlarms>
                    <div
                        *ngIf="current.configuration.state !== 'idle' && current.configuration.state !== 'loading'"
                        class="empty-state"
                    >
                        {{ unavailableText }}
                    </div>
                </ng-template>
            </div>
        </div>
        <div class="resource-error" *ngIf="hasResourceError('location') || hasResourceError('configuration')">
            {{ 'DIAGNOSTICS.FLEX_MONITOR_STATUS.CONFIGURATION_ERROR' | translate }}
        </div>
    </section>

    <section class="status-section" aria-labelledby="flex-sensors-heading">
        <h2 id="flex-sensors-heading">{{ 'DIAGNOSTICS.FLEX_MONITOR_STATUS.CONNECTED_SENSORS' | translate }}</h2>
        <mat-accordion multi *ngIf="configuredSensors.length">
            <mat-expansion-panel *ngFor="let sensor of configuredSensors">
                <mat-expansion-panel-header class="sensor-panel-header">
                    <mat-panel-title>
                        {{ sensor.configuration.sensorName || ('DIAGNOSTICS.FLEX_MONITOR_STATUS.SENSOR' | translate) }}
                    </mat-panel-title>
                </mat-expansion-panel-header>
                <div class="detail-grid sensor-detail-grid">
                    <div class="detail-cell">
                        <span>{{ 'DIAGNOSTICS.FLEX_MONITOR_STATUS.SENSOR_TYPE' | translate }}</span>
                        <strong>{{ sensor.configuration.sensorName || sensor.configuration.sensorType }}</strong>
                    </div>
                    <div class="detail-cell">
                        <span>{{ 'DIAGNOSTICS.FLEX_MONITOR_STATUS.CHANNEL' | translate }}</span>
                        <strong>CH{{ sensor.configuration.channel }}</strong>
                    </div>
                    <div class="detail-cell">
                        <span>{{ 'DIAGNOSTICS.FLEX_MONITOR_STATUS.MONITORING_POINT' | translate }}</span>
                        <strong>MP1</strong>
                    </div>
                    <div class="detail-cell">
                        <span>{{ 'DIAGNOSTICS.FLEX_MONITOR_STATUS.PHYSICAL_SENSOR_SERIAL' | translate }}</span>
                        <div class="copyable-value">
                            <strong class="identifier">{{
                                sensor.physicalSensorSerialNumber || unavailableText
                            }}</strong>
                            <button
                                *ngIf="canCopy(sensor.physicalSensorSerialNumber)"
                                mat-icon-button
                                type="button"
                                class="copy-button non-printable"
                                [class.copied]="isCopied('physicalSensorSerial', sensor.physicalSensorSerialNumber)"
                                [attr.aria-label]="
                                    copyActionText(
                                        'physicalSensorSerial',
                                        sensor.physicalSensorSerialNumber,
                                        'DIAGNOSTICS.FLEX_MONITOR_STATUS.PHYSICAL_SENSOR_SERIAL'
                                    )
                                "
                                [matTooltip]="
                                    copyActionText(
                                        'physicalSensorSerial',
                                        sensor.physicalSensorSerialNumber,
                                        'DIAGNOSTICS.FLEX_MONITOR_STATUS.PHYSICAL_SENSOR_SERIAL'
                                    )
                                "
                                (click)="
                                    copyValue(
                                        sensor.physicalSensorSerialNumber,
                                        'physicalSensorSerial',
                                        'DIAGNOSTICS.FLEX_MONITOR_STATUS.PHYSICAL_SENSOR_SERIAL'
                                    )
                                "
                            >
                                <mat-icon>{{
                                    isCopied('physicalSensorSerial', sensor.physicalSensorSerialNumber)
                                        ? 'check'
                                        : 'content_copy'
                                }}</mat-icon>
                            </button>
                        </div>
                    </div>
                    <div class="detail-cell">
                        <span>{{ 'DIAGNOSTICS.FLEX_MONITOR_STATUS.SENSOR_REPORTED_TIME' | translate }}</span>
                        <strong>{{ formatStatusTimestamp(sensor.reportedAtUtc) }}</strong>
                    </div>
                    <div class="detail-cell">
                        <span>{{ 'DIAGNOSTICS.FLEX_MONITOR_STATUS.DEPTH_PREFERENCE' | translate }}</span>
                        <strong>{{ sensor.isDepthPreference ? sensor.depthPreference : unavailableText }}</strong>
                    </div>
                    <div class="detail-cell">
                        <span>{{ 'DIAGNOSTICS.FLEX_MONITOR_STATUS.VELOCITY_PREFERENCE' | translate }}</span>
                        <strong>{{ sensor.isVelocityPreference ? sensor.velocityPreference : unavailableText }}</strong>
                    </div>
                </div>
                <div class="resource-error" *ngIf="sensor.identityAmbiguous">
                    {{ 'DIAGNOSTICS.FLEX_MONITOR_STATUS.SENSOR_IDENTITY_AMBIGUOUS' | translate }}
                </div>
            </mat-expansion-panel>
        </mat-accordion>
        <div *ngIf="!configuredSensors.length && current.sensors.state === 'empty'" class="empty-state">
            {{ 'DIAGNOSTICS.FLEX_MONITOR_STATUS.NO_MP1_SENSORS' | translate }}
        </div>
        <div *ngIf="!configuredSensors.length && current.sensors.state === 'blocked'" class="empty-state">
            {{ 'DIAGNOSTICS.FLEX_MONITOR_STATUS.SENSOR_LOOKUP_UNAVAILABLE' | translate }}
        </div>

        <div class="readings-heading">
            <h3>{{ 'DIAGNOSTICS.FLEX_MONITOR_STATUS.MP1_READINGS' | translate }}</h3>
            <div class="action-buttons non-printable">
                <button
                    mat-button
                    type="button"
                    [class.waiting]="isWaitingForSensor"
                    [disabled]="current.isLoading || isWaitingForSensor"
                    (click)="refresh()"
                >
                    <mat-icon>refresh</mat-icon>
                    <span class="refresh-label">
                        <span [class.hidden-label]="isWaitingForSensor">{{
                            'DIAGNOSTICS.FLEX_MONITOR_STATUS.REFRESH' | translate
                        }}</span>
                        <span [class.hidden-label]="!isWaitingForSensor" class="waiting-label">
                            {{ 'DIAGNOSTICS.FLEX_MONITOR_STATUS.WAITING_FOR_SENSOR' | translate }}
                            <span class="waiting-dots" aria-hidden="true">
                                <span>.</span>
                                <span>.</span>
                                <span>.</span>
                            </span>
                        </span>
                    </span>
                </button>
            </div>
        </div>
        <div *ngIf="current.hydrograph.state !== 'notAuthorized'" class="reading-table-container">
            <table class="reading-table">
                <thead>
                    <tr>
                        <th>{{ 'DIAGNOSTICS.FLEX_MONITOR_STATUS.MEASUREMENT' | translate }}</th>
                        <th>{{ 'DIAGNOSTICS.FLEX_MONITOR_STATUS.VALUE' | translate }}</th>
                        <th>{{ 'COMMON.TIMESTAMP' | translate }}</th>
                    </tr>
                </thead>
                <tbody>
                    <tr *ngFor="let readingItem of readingItems">
                        <th scope="row">{{ readingItem.labelKey | translate }}</th>
                        <td>{{ formatReading(reading(readingItem.id)) }}</td>
                        <td>{{ formatReadingTimestamp(reading(readingItem.id)) }}</td>
                    </tr>
                </tbody>
            </table>
        </div>
        <div *ngIf="current.hydrograph.state === 'notAuthorized'" class="empty-state">
            {{ 'DIAGNOSTICS.FLEX_MONITOR_STATUS.RAW_DATA_REQUIRED' | translate }}
        </div>
        <ng-container *ngIf="diagnosticTable as table">
            <h4 class="diagnostic-heading">
                {{ 'DIAGNOSTICS.FLEX_MONITOR_STATUS.SENSOR_DIAGNOSTICS' | translate }}
                <span>{{
                    table.sensor.configuration.sensorName || ('DIAGNOSTICS.FLEX_MONITOR_STATUS.SENSOR' | translate)
                }}</span>
                <span *ngIf="table.sensor.physicalSensorSerialNumber" class="identifier">{{
                    table.sensor.physicalSensorSerialNumber
                }}</span>
            </h4>
            <div *ngIf="table.latest as latest" class="reading-table-container">
                <table class="reading-table diagnostic-table">
                    <thead>
                        <tr>
                            <th>{{ 'DIAGNOSTICS.FLEX_MONITOR_STATUS.MEASUREMENT' | translate }}</th>
                            <th *ngIf="table.previous as previous" class="previous-result">
                                {{ previous.labelKey | translate }}
                                <div class="diagnostic-summary">
                                    {{ formatDiagnosticTimestamp(previous.completedAt) }}
                                </div>
                            </th>
                            <th [class.new-result]="table.previous !== null">
                                {{ latest.labelKey | translate }}
                                <div *ngIf="latest.completedAt" class="diagnostic-summary">
                                    {{ formatDiagnosticTimestamp(latest.completedAt) }}
                                </div>
                                <div *ngIf="latest.state === 'failed'" class="diagnostic-summary diagnostic-failed">
                                    {{ 'DIAGNOSTICS.FLEX_MONITOR_STATUS.DIAGNOSTIC_FAILED' | translate }}
                                </div>
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr *ngFor="let row of table.readings">
                            <th scope="row">{{ row.sensor }}</th>
                            <td *ngIf="table.previous" class="previous-result">
                                {{ formatDiagnosticReading(row.previous, row) }}
                            </td>
                            <td [class.new-result]="table.previous !== null">
                                {{ formatDiagnosticReading(row.latest, row) }}
                                <span *ngIf="row.change !== null" class="diagnostic-change">
                                    <mat-icon aria-hidden="true">{{
                                        row.change > 0 ? 'arrow_drop_up' : 'arrow_drop_down'
                                    }}</mat-icon>
                                    {{ formatDiagnosticChange(row.change, row.precision) }}
                                </span>
                            </td>
                        </tr>
                        <tr *ngFor="let row of table.angles">
                            <th scope="row">{{ row.labelKey | translate }}</th>
                            <td *ngIf="table.previous" class="previous-result">
                                {{ formatDiagnosticAngle(row.previous, row.precision) }}
                                <div *ngIf="row.previous?.isOutsideRange" class="angle-warning-text">
                                    {{ 'DIAGNOSTICS.FLEX_MONITOR_STATUS.ANGLE_WARNING' | translate }}
                                </div>
                            </td>
                            <td [class.new-result]="table.previous !== null">
                                {{ formatDiagnosticAngle(row.latest, row.precision) }}
                                <span *ngIf="row.change !== null" class="diagnostic-change">
                                    <mat-icon aria-hidden="true">{{
                                        row.change > 0 ? 'arrow_drop_up' : 'arrow_drop_down'
                                    }}</mat-icon>
                                    {{ formatDiagnosticChange(row.change, row.precision) }}
                                </span>
                                <div *ngIf="row.latest?.isOutsideRange" class="angle-warning-text">
                                    {{ 'DIAGNOSTICS.FLEX_MONITOR_STATUS.ANGLE_WARNING' | translate }}
                                </div>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </ng-container>
        <div class="resource-error" *ngIf="hasResourceError('sensors') || hasResourceError('hydrograph')">
            {{ 'DIAGNOSTICS.FLEX_MONITOR_STATUS.SENSOR_READING_ERROR' | translate }}
        </div>
    </section>

    <section class="status-section" aria-labelledby="flex-data-network-heading">
        <h2 id="flex-data-network-heading">{{ 'DIAGNOSTICS.FLEX_MONITOR_STATUS.DATA_NETWORK_STATUS' | translate }}</h2>
        <div class="detail-grid data-network-grid">
            <div class="detail-cell">
                <span>{{ 'DIAGNOSTICS.FLEX_MONITOR_STATUS.MONITOR_DATA_START' | translate }}</span>
                <strong>{{ formatStatusTimestamp(status?.storage?.oldestEntryTimeUtc?.value) }}</strong>
            </div>
            <div class="detail-cell">
                <span>{{ 'DIAGNOSTICS.FLEX_MONITOR_STATUS.MONITOR_DATA_END' | translate }}</span>
                <strong>{{ formatStatusTimestamp(status?.storage?.newestEntryTimeUtc?.value) }}</strong>
            </div>
            <div class="detail-cell">
                <span>{{ 'DIAGNOSTICS.FLEX_MONITOR_STATUS.LAST_REPORTED' | translate }}</span>
                <strong>{{ formatStatusTimestamp(status?.timestamps?.lastReportedAtUtc) }}</strong>
            </div>
        </div>
    </section>
</section>
